import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CategoryBadge } from "../components/Badges";
import { CATEGORIES, CATEGORY_LABEL, relTime } from "../lib/labels";
import RichEditor from "../components/RichEditor";
import RichView from "../components/RichView";
import { ArrowLeft, Eye, Paperclip, UploadSimple, Trash, DownloadSimple, FilePdf, ImageSquare,
  File as FileIcon, PencilSimple, FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { ConfirmDialog } from "../components/ConfirmDialog";

// Build a download URL carrying the JWT as a query param — mandatory because <a target=_blank>
// links don't send the Authorization header, so private/internal attachments would 401.
function attachmentUrl(attId) {
  const token = localStorage.getItem("helios_token");
  const base = `${API_BASE}/kb/attachments/${attId}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

const VISIBILITIES = [
  { v: "public", label: "Public (accessible sans compte)" },
  { v: "authenticated", label: "Connectés (clients + staff)" },
  { v: "internal", label: "Interne (staff uniquement)" },
];

function humanSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

function AttachIcon({ ct }) {
  if (!ct) return <FileIcon size={16}/>;
  if (ct.startsWith("image/")) return <ImageSquare size={16}/>;
  if (ct === "application/pdf") return <FilePdf size={16}/>;
  return <FileIcon size={16}/>;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "technicien";
  const isAdmin = user?.role === "admin";
  const [a, setA] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [attachToDelete, setAttachToDelete] = useState(null);
  const fileRef = useRef();

  const load = () => api.get(`/kb/articles/${id}`).then((r) => setA(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openEdit = () => {
    setForm({
      title: a.title,
      content: a.content,
      category: a.category,
      visibility: a.visibility || "authenticated",
      tags: (a.tags || []).join(", "),
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/kb/articles/${id}`, {
        title: form.title,
        content: form.content,
        category: form.category,
        visibility: form.visibility,
        is_html: true,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Article mis à jour");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const removeArticle = async () => {
    try {
      await api.delete(`/kb/articles/${id}`);
      toast.success("Article envoyé à la corbeille", {
        description: "Restauration possible pendant 30 jours",
      });
      nav("/kb");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/kb/articles/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Pièce jointe ajoutée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttachment = async () => {
    if (!attachToDelete) return;
    await api.delete(`/kb/articles/${id}/attachments/${attachToDelete.id}`);
    toast.success("Pièce jointe supprimée");
    setAttachToDelete(null);
    load();
  };

  if (!a) return <div className="text-muted-foreground">Chargement…</div>;

  const attachments = a.attachments || [];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/kb" className="text-sm text-muted-foreground flex items-center gap-1" data-testid="back-kb">
        <ArrowLeft size={16}/> Base de connaissances
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <CategoryBadge value={a.category}/>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">{a.title}</h1>
          <div className="text-sm text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
            <span>{a.author_name}</span>
            <span>·</span>
            <span>{relTime(a.updated_at)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Eye size={12}/> {a.views}</span>
            {a.visibility && a.visibility !== "authenticated" && (
              <>
                <span>·</span>
                <span className="text-[0.65rem] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted">{a.visibility}</span>
              </>
            )}
          </div>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="secondary" onClick={openEdit} data-testid="kb-edit-btn">
              <PencilSimple size={14} className="mr-1.5"/> Modifier
            </Button>
            {isAdmin && (
              <Button size="sm" variant="destructive" onClick={() => setConfirmDeleteOpen(true)} data-testid="kb-delete-btn">
                <Trash size={14} className="mr-1.5"/> Supprimer
              </Button>
            )}
          </div>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          {a.is_html
            ? <RichView html={a.content} className="prose max-w-none leading-relaxed"/>
            : <article className="prose max-w-none whitespace-pre-wrap leading-relaxed">{a.content}</article>
          }
        </CardContent>
      </Card>

      {/* Attachments */}
      <section data-testid="kb-attachments">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Paperclip size={18}/> Pièces jointes <span className="text-muted-foreground text-sm">({attachments.length})</span>
          </h2>
          {isStaff && (
            <>
              <input ref={fileRef} type="file" multiple className="hidden"
                data-testid="kb-attach-input"
                onChange={(e) => e.target.files?.length && uploadFiles(Array.from(e.target.files))}/>
              <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="kb-attach-btn">
                <UploadSimple size={14} className="mr-1.5"/> {uploading ? "Envoi…" : "Ajouter"}
              </Button>
            </>
          )}
        </div>
        {attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune pièce jointe.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {attachments.map((att) => (
              <Card key={att.id} data-testid={`kb-att-${att.id}`}>
                <CardContent className="py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <AttachIcon ct={att.content_type}/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{att.original_filename}</div>
                    <div className="text-xs text-muted-foreground">{humanSize(att.size)} · {relTime(att.created_at)}</div>
                  </div>
                  <a href={attachmentUrl(att.id)} target="_blank" rel="noopener noreferrer"
                    className="p-2 rounded-md hover:bg-muted" title="Télécharger" data-testid={`kb-att-dl-${att.id}`}>
                    <DownloadSimple size={16}/>
                  </a>
                  {isStaff && (
                    <button onClick={() => setAttachToDelete(att)}
                      className="p-2 rounded-md hover:bg-destructive/10 text-destructive"
                      title="Supprimer" data-testid={`kb-att-del-${att.id}`}>
                      <Trash size={16}/>
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {a.tags?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {a.tags.map((t) => <span key={t} className="text-xs px-2 py-1 bg-muted rounded-full">#{t}</span>)}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Modifier l&apos;article</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input required data-testid="kb-edit-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="kb-edit-category"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibilité</Label>
                  <Select value={form.visibility} onValueChange={(v) => setForm({...form, visibility: v})}>
                    <SelectTrigger data-testid="kb-edit-visibility"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {VISIBILITIES.map((v) => <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tags (virgules)</Label>
                  <Input data-testid="kb-edit-tags" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}/>
                </div>
              </div>
              <div>
                <Label>Contenu</Label>
                <div className="mt-1">
                  <RichEditor value={form.content} onChange={(v) => setForm({...form, content: v})} minHeight={280}/>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="kb-edit-save">
                  <FloppyDisk size={14} className="mr-1.5"/> {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Envoyer à la corbeille ?"
        description={`« ${a.title} » sera déplacé dans la corbeille. Vous pourrez le restaurer pendant 30 jours.`}
        confirmLabel="Envoyer à la corbeille"
        danger
        onConfirm={removeArticle}
        testIdPrefix="kb-del-confirm"
      />

      <ConfirmDialog
        open={!!attachToDelete}
        onOpenChange={(v) => !v && setAttachToDelete(null)}
        title="Supprimer la pièce jointe ?"
        description={attachToDelete && `« ${attachToDelete.original_filename} » sera supprimé de façon permanente.`}
        confirmLabel="Supprimer"
        danger
        onConfirm={removeAttachment}
        testIdPrefix="att-del-confirm"
      />
    </div>
  );
}
